import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { motion } from 'framer-motion';
import { Receipt, PiggyBank, FileText, TrendingUp, Plus, ArrowRight, DollarSign, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SpendingChart from '../components/insights/SpendingChart';
import CategoryBreakdown from '../components/insights/CategoryBreakdown';
import RecurringExpenses from '../components/recurring/RecurringExpenses';

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { user } = useAuth();
  const { data: receipts = [], refetch: refetchReceipts } = useQuery({
    queryKey: ['receipts'],
    queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; }
  });

  const { data: budgets = [] } = useQuery({
    queryKey: ['budgets'],
    queryFn: async () => { const { data } = await api.get('/api/budgets'); return data; }
  });

  const now = new Date();
  const lastMonth = subMonths(now, 1);
  const thisMonthReceipts = receipts.filter(r => { const d = new Date(r.date); return d >= startOfMonth(now) && d <= endOfMonth(now); });
  const lastMonthReceipts = receipts.filter(r => { const d = new Date(r.date); return d >= startOfMonth(lastMonth) && d <= endOfMonth(lastMonth); });

  const thisMonthTotal = thisMonthReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const lastMonthTotal = lastMonthReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const monthChange = lastMonthTotal > 0 ? ((thisMonthTotal - lastMonthTotal) / lastMonthTotal * 100) : 0;
  const taxDeductible = thisMonthReceipts.filter(r => r.tax_deductible).reduce((s, r) => s + (r.amount * (r.tax_split_percentage || 100) / 100), 0);
  const gstCredits = thisMonthReceipts.filter(r => r.gst_type === 'gst_10').reduce((s, r) => s + (r.gst_amount || r.amount / 11), 0);

  const stats = [
    { title: 'This Month', value: fmt(thisMonthTotal), icon: DollarSign, color: 'emerald', trend: monthChange >= 0 ? 'up' : 'down', trendValue: `${Math.abs(monthChange).toFixed(0)}%` },
    { title: 'Receipts', value: thisMonthReceipts.length, icon: Receipt, color: 'blue' },
    { title: 'Tax Deductible', value: fmt(taxDeductible), icon: FileText, color: 'purple' },
    { title: 'GST Credits', value: fmt(gstCredits), icon: TrendingUp, color: 'orange' },
  ];

  const colors = {
    emerald: 'from-emerald-500 to-teal-500',
    blue: 'from-blue-500 to-indigo-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-amber-500',
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              G'day, {user?.name?.split(' ')[0] || 'there'}
            </h1>
            <p className="text-slate-500 mt-1">{format(now, 'EEEE, d MMMM yyyy')}</p>
          </div>
          <Link to="/receipts">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600" data-testid="add-receipt-button">
              <Plus className="w-4 h-4 mr-2" /> Add Receipt
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
              data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colors[stat.color]} opacity-5 rounded-full -translate-y-16 translate-x-16`} />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  {stat.trend && (
                    <div className="flex items-center gap-1 mt-2">
                      {stat.trend === 'up' ? <TrendingUp className="w-4 h-4 text-red-500" /> : <TrendingDown className="w-4 h-4 text-emerald-500" />}
                      <span className={`text-sm font-medium ${stat.trend === 'up' ? 'text-red-500' : 'text-emerald-500'}`}>{stat.trendValue}</span>
                      <span className="text-xs text-slate-400">vs last month</span>
                    </div>
                  )}
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${colors[stat.color]}`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Recurring Expenses */}
        {receipts.length > 0 && (
          <div className="mb-8">
            <RecurringExpenses receipts={receipts} onRefetch={refetchReceipts} />
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Spending Trend</h2>
            <SpendingChart receipts={receipts} months={6} />
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Category Breakdown</h2>
            <CategoryBreakdown receipts={thisMonthReceipts} />
          </div>
        </div>

        {/* Recent Receipts */}
        <div className="bg-white rounded-2xl border border-slate-100">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent Receipts</h2>
            <Link to="/receipts" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-slate-50">
            {receipts.slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Receipt className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{r.store_name}</p>
                    <p className="text-xs text-slate-400">{r.date && format(new Date(r.date), 'dd MMM yyyy')}</p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-slate-900">{fmt(r.amount)}</p>
              </div>
            ))}
            {receipts.length === 0 && (
              <div className="py-10 text-center text-slate-400 text-sm">
                No receipts yet. Start by adding your first receipt!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
